import { useMemo, useState, type FormEvent } from 'react';
import { Button, Field, Icon, Modal } from './ui';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { roleLabel } from '../lib/format';
import type { TreeNode } from '../lib/types';

/**
 * How much each role carries, mirrored from ROLE_RANK on the API.
 *
 * Used only to decide what this person may offer in the dropdown — the API
 * applies the same rule again and refuses anything above the inviter's own
 * rank, so a tampered form gets a 403 rather than a promotion.
 */
const ROLE_RANK: Record<string, number> = {
  merchant_support: 1,
  l4_agent: 2,
  l3_manager: 3,
  l2_office: 4,
  l1_underwriter: 4,
  l1_admin: 5,
};

const ROLE_HINT: Record<string, string> = {
  l4_agent: 'Writes applications and sees only their own files.',
  l3_manager: 'Runs an agency and sees its whole downline.',
  l2_office: 'Runs an office: can restructure the tree and export residuals.',
  l1_underwriter: 'Approves and declines applications; cannot author one.',
  l1_admin: 'Everything, everywhere.',
  merchant_support: 'Supplies ordering only.',
};

interface InviteResult {
  id: string;
  email: string;
  expiresAt?: string;
  emailed: boolean;
  /** Returned only when no mail relay is configured. */
  inviteUrl?: string;
}

/** Flattens the tree into a list the office dropdown can show, with depth. */
function flatten(node: TreeNode, depth = 0): Array<{ node: TreeNode; depth: number }> {
  return [
    { node, depth },
    ...(node.children ?? []).flatMap((child) => flatten(child, depth + 1)),
  ];
}

/**
 * Inviting someone into the organisation.
 *
 * There is no "create a user with a password" anywhere in this portal, on
 * purpose: an account nobody but its owner has ever held the password to is
 * worth more than the convenience. This sends a single-use link, and the
 * person sets their own password and second factor at the other end.
 */
export function InvitePerson({
  open,
  onClose,
  onInvited,
  tree,
  defaultNodeId,
}: {
  open: boolean;
  onClose: () => void;
  onInvited?: () => void;
  tree: TreeNode | null;
  /** Pre-selects the office the invitation was started from. */
  defaultNodeId?: string;
}) {
  const { user } = useAuth();
  const nodes = useMemo(() => (tree ? flatten(tree) : []), [tree]);

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('l4_agent');
  const [nodeId, setNodeId] = useState(defaultNodeId ?? user?.nodeId ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<InviteResult | null>(null);
  const [copied, setCopied] = useState(false);

  // You cannot create someone who outranks you; the API enforces it too.
  const ceiling = ROLE_RANK[user?.role ?? ''] ?? 0;
  const roles = Object.keys(ROLE_RANK).filter((r) => ROLE_RANK[r] <= ceiling);

  function reset() {
    setEmail('');
    setName('');
    setResult(null);
    setError(null);
    setCopied(false);
  }

  /**
   * Closing, and only then telling the page to refresh.
   *
   * The parent refetches its list when someone is invited, and blanks the
   * screen for a loader while it does — which unmounts this dialog and takes
   * the confirmation with it. Deferring the refresh keeps the invitation, and
   * the link it may be carrying, on screen until it has been read.
   */
  function close() {
    const created = result !== null;
    reset();
    onClose();
    if (created) onInvited?.();
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<InviteResult>('/org/invitations', {
        email: email.trim(),
        nodeId: nodeId || user?.nodeId,
        role,
        ...(name.trim() ? { name: name.trim() } : {}),
      });
      setResult(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      title={result ? 'Invitation created' : 'Invite a person'}
      description={
        result
          ? undefined
          : 'They choose their own password and second factor from the link — nobody else ever holds them.'
      }
      onClose={close}
    >
      {result ? (
        <div className="stack">
          {result.emailed ? (
            <div className="notice notice--ok" role="status">
              {Icon.check({ size: 15 })}
              <span>
                Sent to <strong>{result.email}</strong>. The link works once and
                expires in three days.
              </span>
            </div>
          ) : (
            <>
              <div className="notice notice--warn">
                {Icon.alert({ size: 15 })}
                <span>
                  No mail relay is configured, so nothing was sent. Give them
                  this link yourself — it works once.
                </span>
              </div>
              <code className="secretkey">{result.inviteUrl}</code>
              <div className="row">
                <Button
                  onClick={() => {
                    void navigator.clipboard?.writeText(result.inviteUrl ?? '');
                    setCopied(true);
                  }}
                >
                  {copied ? 'Copied' : 'Copy link'}
                </Button>
              </div>
            </>
          )}

          <div className="row">
            <Button variant="primary" onClick={reset}>
              Invite someone else
            </Button>
            <Button
              onClick={() => {
                close();
              }}
            >
              Done
            </Button>
          </div>
        </div>
      ) : (
        <form className="stack" onSubmit={submit}>
          {error && (
            <div className="notice notice--danger" role="alert">
              {Icon.alert({ size: 15 })}
              <span>{error}</span>
            </div>
          )}

          <Field label="Work email" htmlFor="invite-email">
            <input
              id="invite-email"
              className="input"
              type="email"
              required
              autoComplete="off"
              placeholder="name@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>

          <Field
            label="Full name"
            hint="Optional — they can set it themselves when they accept."
            htmlFor="invite-name"
          >
            <input
              id="invite-name"
              className="input"
              autoComplete="off"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>

          <Field label="Role" hint={ROLE_HINT[role]} htmlFor="invite-role">
            <select
              id="invite-role"
              className="input select"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              {roles.map((r) => (
                <option key={r} value={r}>
                  {roleLabel(r)}
                </option>
              ))}
            </select>
          </Field>

          <Field
            label="Team or office"
            hint="Anywhere at or beneath your own. An agent's own team is named after them — their scope follows from whichever you pick."
            htmlFor="invite-node"
          >
            <select
              id="invite-node"
              className="input select"
              value={nodeId}
              onChange={(e) => setNodeId(e.target.value)}
            >
              {nodes.map(({ node, depth }) => (
                <option key={node.id} value={node.id}>
                  {'  '.repeat(depth)}
                  {node.name}
                  <> · {node.level}</>
                </option>
              ))}
            </select>
          </Field>

          <div className="row">
            <Button type="submit" variant="primary" disabled={busy || !email}>
              {busy ? 'Sending…' : 'Send invitation'}
            </Button>
            <Button
              type="button"
              onClick={() => {
                close();
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
